import React, { useState } from 'react'
import { Input, Spinner, Typography, Button } from '@material-tailwind/react'
import { PiGiftLight } from 'react-icons/pi'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSessionContext } from '../contexts/SessionContext'
import { redeemUpgradeCode, updateUser } from '../utils/helpers/supabase.helpers'
import RedeemCodeMessage from './RedeemCodeMessage'
import { useGeneralToast } from '../hooks/toast'

const RedeemCodePanel: React.FC = () => {
  const { t } = useTranslation()
  const { refresh, user } = useSessionContext()
  const { error: toastError, success: toastSuccess } = useGeneralToast()
  const [searchParams] = useSearchParams()
  const codeParam = searchParams.get('redeemCode')
  const [redeemCode, setRedeemCode] = useState<string | null>(codeParam)
  const [redeemCodeError, setRedeemCodeError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const handleRedeemCode = async (code: string | null) => {
    if (!code || !user?.email || !user?.id) {
      return
    }

    setLoading(true)

    const { data, error } = await redeemUpgradeCode({
      code,
      email: user.email,
    })

    if (error) {
      setRedeemCodeError(typeof error === 'string' ? error : (error as Error)?.message || 'Unknown error')
      setLoading(false)
      return
    }

    const redeemedRole = (data as { target_role?: number })?.target_role

    const { error: updateUserError } = await updateUser({
      attributes: { role_id: redeemedRole },
      id: user.id,
    })

    if (updateUserError) {
      toastError(
        typeof updateUserError === 'string'
          ? updateUserError
          : ((updateUserError as Error)?.message ?? t('redeemCode.somethingWentWrong'))
      )
      setLoading(false)
      return
    }

    toastSuccess(t('redeemCode.upgradeSuccess'))

    setRedeemCode(null)

    refresh()
  }

  return (
    <div className="w-full mt-6">
      <Typography variant="h5" className="mb-2">
        {t('redeemCode.title')}
      </Typography>
      <Input
        className="w-full font-bold"
        value={redeemCode || ''}
        onChange={({ currentTarget }) => {
          setRedeemCodeError(null)
          setRedeemCode(currentTarget.value)
        }}
        onKeyDown={({ code }) => {
          if (code === 'Enter') {
            handleRedeemCode(redeemCode)
          }
        }}
        type="text"
        label={t('redeemCode.codeLabel')}
        success={!!redeemCode && !redeemCodeError}
        error={!!redeemCode && !!redeemCodeError}
        data-testid="membership-upgrade-code-input"
      />
      {redeemCode && (
        <>
          <RedeemCodeMessage
            redeemCodeError={redeemCodeError}
            setRedeemCode={setRedeemCode}
            setRedeemCodeError={setRedeemCodeError}
            urlParamCode={codeParam}
            textColor="white"
          />
          <Button
            className="flex flex-grow-0 items-center gap-2 mt-2"
            size="sm"
            onClick={() => handleRedeemCode(redeemCode)}
            disabled={loading || !!redeemCodeError}
          >
            {loading ? <Spinner className="h-4 w-4" /> : <PiGiftLight className="text-lg" />}{' '}
            {t('redeemCode.redeemButton')}
          </Button>
        </>
      )}
    </div>
  )
}

export default RedeemCodePanel
